import { useTranslation } from '@pulsex/localization'
import {
  Button,
  Flex,
  IconButton,
  AddIcon,
  MinusIcon,
  useModal,
  useToast
} from '@pulsex/uikit'
import { BIG_ZERO } from '@pulsex/utils/bigNumber'
import { FarmWidget } from '@pulsex/widgets-internal'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCatchTxError from 'hooks/useCatchTxError'
import { useCallback } from 'react'
import { styled } from 'styled-components'
import { useLocation } from 'react-router'
import { usePriceINCUsdc } from 'state/farms/hooks'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import { WNATIVE, NATIVE } from '@pulsex/sdk'
import { FarmWithStakedValue } from '@pulsex/farms'

interface FarmCardActionsProps extends FarmWithStakedValue {
  lpLabel?: string
  addLiquidityUrl?: string
  displayApr?: string
  onStake: <SendTransactionResult>(value: string) => Promise<SendTransactionResult>
  onUnstake: <SendTransactionResult>(value: string) => Promise<SendTransactionResult>
  onDone: () => void
  onApprove: <SendTransactionResult>() => Promise<SendTransactionResult>
  isApproved: boolean
}

const IconButtonWrapper = styled.div`
  display: flex;
  svg {
    width: 20px;
  }
`

const StakeAction: React.FC<React.PropsWithChildren<FarmCardActionsProps>> = ({
  pid,
  quoteToken,
  token,
  lpSymbol,
  lpTokenPrice,
  multiplier,
  apr,
  displayApr,
  addLiquidityUrl,
  lpLabel,
  lpTotalSupply,
  tokenAmountTotal,
  quoteTokenAmountTotal,
  userData,
  onStake,
  onUnstake,
  onDone,
  onApprove,
  isApproved,
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { account, chainId } = useAccountActiveChain()
  const { tokenBalance, stakedBalance, allowance } = userData || {}
  const incPrice = usePriceINCUsdc()
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()

  const handleStake = async (amount: string) => {
    const receipt = await fetchWithCatchTxError(() => onStake(amount))
    if (receipt?.status) {
      toastSuccess(
        `${t('Staked')}!`,
        <ToastDescriptionWithTx txHash={receipt.transactionHash}>
          {t('Your funds have been staked in the farm')}
        </ToastDescriptionWithTx>,
      )
      onDone()
    }
  }

  const handleUnstake = async (amount: string) => {
    const receipt = await fetchWithCatchTxError(() => onUnstake(amount))
    if (receipt?.status) {
      toastSuccess(
        `${t('Unstaked')}!`,
        <ToastDescriptionWithTx txHash={receipt.transactionHash}>
          {t('Your earnings have also been harvested to your wallet')}
        </ToastDescriptionWithTx>,
      )
      onDone()
    }
  }

  const handleApprove = useCallback(async () => {
    const receipt = await fetchWithCatchTxError(() => {
      return onApprove()
    })
    if (receipt?.status) {
      toastSuccess(t('Contract Enabled'), <ToastDescriptionWithTx txHash={receipt.transactionHash} />)
      onDone()
    }
  }, [onApprove, t, toastSuccess, fetchWithCatchTxError, onDone])

  const [onPresentDeposit] = useModal(
    <FarmWidget.DepositModal
      account={account}
      pid={pid}
      lpTotalSupply={lpTotalSupply ?? BIG_ZERO}
      max={tokenBalance ?? BIG_ZERO}
      stakedBalance={stakedBalance ?? BIG_ZERO}
      tokenName={lpSymbol}
      multiplier={multiplier}
      lpPrice={lpTokenPrice}
      lpLabel={lpLabel}
      apr={apr}
      displayApr={displayApr}
      addLiquidityUrl={addLiquidityUrl}
      incPrice={incPrice}
      decimals={18}
      allowance={allowance}
      enablePendingTx={pendingTx}
      onConfirm={handleStake}
      handleApprove={handleApprove}
    />,
    true,
    true,
    `farm-deposit-modal-${pid}`,
  )
  const [onPresentWithdraw] = useModal(
    <FarmWidget.WithdrawModal
      token={token}
      quoteToken={quoteToken}
      max={stakedBalance ?? BIG_ZERO}
      onConfirm={handleUnstake}
      lpPrice={lpTokenPrice}
      tokenName={lpSymbol}
      decimals={18}
    />,
  )

  const renderStakingButtons = () => {
    return stakedBalance?.eq(0) ? (
      <Button
        onClick={onPresentDeposit}
        disabled={['history', 'archived'].some((item) => pathname.includes(item))}
      >
        {t('Stake LP')}
      </Button>
    ) : (
      <IconButtonWrapper>
        <IconButton variant="tertiary" onClick={onPresentWithdraw} mr="6px">
          <MinusIcon color="primary" width="14px" />
        </IconButton>
        <IconButton
          variant="tertiary"
          onClick={onPresentDeposit}
          disabled={['history', 'archived'].some((item) => pathname.includes(item))}
        >
          <AddIcon color="primary" width="14px" />
        </IconButton>
      </IconButtonWrapper>
    )
  }

  // TODO: Move this out to prevent unnecessary re-rendered
  if (!isApproved && stakedBalance?.eq(0)) {
    return (
      <Button mt="8px" width="100%" disabled={pendingTx} onClick={handleApprove}>
        {t('Enable Contract')}
      </Button>
    )
  }

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <FarmWidget.StakedLP
        decimals={18}
        stakedBalance={stakedBalance ?? BIG_ZERO}
        quoteTokenSymbol={WNATIVE[chainId]?.symbol === quoteToken.symbol ? NATIVE[chainId]?.symbol : quoteToken.symbol}
        tokenSymbol={WNATIVE[chainId]?.symbol === token.symbol ? NATIVE[chainId]?.symbol : token.symbol}
        lpTotalSupply={lpTotalSupply ?? BIG_ZERO}
        lpTokenPrice={lpTokenPrice ?? BIG_ZERO}
        tokenAmountTotal={tokenAmountTotal ?? BIG_ZERO}
        quoteTokenAmountTotal={quoteTokenAmountTotal ?? BIG_ZERO}
        pendingFarmLength={0}
      />
      {renderStakingButtons()}
    </Flex>
  )
}

export default StakeAction
